/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, DoCheck, Input, OnInit} from '@angular/core';
import {NgClass} from '@angular/common';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {LookupService} from '../../common/service/lookup.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {equals} from '../../common/service/data-util';
import {AppConfigService} from '../../common/service/app-config.service';
import {catchError, EMPTY, finalize, firstValueFrom, map, Observable, of, throwError} from 'rxjs';
import {COMMON_PROPERTY, PDF_CREATION_METHOD, SP_PREFIXED_PARENT_PLAN_TYPES} from '../../common/constants';
import {HttpClient} from '@angular/common/http';
import {AlertModalService, ConfirmationModalService} from '../../common/component/modal';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {InterestService} from '../../design/services/features/interest.service';
import {NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';
import {switchMap} from 'rxjs/operators';
import {PlanPurposeLookup} from '../../common/models/plan-purpose.model';

@Component({
    selector: 'tenure-interests-notifications',
    imports: [FormsModule, NlrHasPermissionsPipe, NlrPermissionsDisabledPipe, NlrFormFieldHasErrorPipe, NgClass],
    templateUrl: './tenure-interests-notifications.component.html',
    styleUrl: './tenure-interests-notifications.component.scss',
    // Enrol this component's ngModel controls in the parent interestsForm so their required validation is
    // aggregated by InterestsComponent.formInvalid() - NgModel resolves its ControlContainer with @Host()
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class TenureInterestsNotificationsComponent implements DoCheck, OnInit {
    title = 'Former Tenure Interests and Notifications';
    formerTenureInterestsNotifications = [];
    nonDigitalTitlesForNotificationTenures = [];
    paperTitleNonDigitalFTNs = [];
    missingNotificationTenures = [];
    noTitleReferences = [];
    populateFormerTenuresInProgress: boolean = false;
    formerTenureNotificationLookups: [];
    notificationTreatmentActionLookups: [];
    formerTenureUiConfig: unknown;
    get readOnly() {
        return this.planModelService.readonly;
    }
    partiallySurrenderedViewOptions = [
        {value: true, displayValue: 'Yes', toolTip: 'Yes'},
        {value: false, displayValue: 'No', toolTip: 'No'}
    ];
    titleExistenceViewOptions = [
        {value: true, displayValue: 'Yes', toolTip: 'Yes'},
        {value: false, displayValue: 'No', toolTip: 'No'}
    ];
    bringForwardStagedSubdivisionInterestsViewOptions = [
        {value: true, displayValue: 'Yes', toolTip: 'Yes'},
        {value: false, displayValue: 'No', toolTip: 'No'}
    ];
    bringForwardStagedSubdivisionInterestsTenures = [];
    allStagedSubdivisionFormerTenures = [];
    private planPurposeLookup: PlanPurposeLookup;

    @Input() interestsUiConfig: unknown;
    invalidFormerTenureText = '';

    constructor(
        private interestService: InterestService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private appConfigService: AppConfigService,
        private http: HttpClient,
        private alertModalService: AlertModalService,
        private confirmationModalService: ConfirmationModalService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit(): void {
        this.formerTenureNotificationLookups = this.lookupService.getLookups('formerTenureNotification');
        this.notificationTreatmentActionLookups = this.lookupService.getLookups('notificationTreatmentAction');
        this.eventBus
            .on<string>('planInformation:invalidFormerTenureText')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(event => {
                this.invalidFormerTenureText = event.data;
            });
    }

    ngDoCheck(): void {
        const planModel = this.planModelService.getModel();
        const formerTenures = planModel.formerTenures;

        const planPurposeLookup = this.lookupService.getLookups('planPurpose').find(v => v.id === planModel.planPurpose.id);
        this.planPurposeLookup = planPurposeLookup;
        this.formerTenureUiConfig = this.lookupService
            .getLookups('formerTenureUiConfig')
            .find(v => v.parentTenureAssociationType === planPurposeLookup.parentTenureAssociationType);

        const isDigitalPlan = planModel.pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN;
        const canBringForwardStagedSubdivision = planPurposeLookup.canBringForwardStagedSubdivisionInterests === true;
        const isStagedSubdivisionCandidate = ft =>
            isDigitalPlan &&
            canBringForwardStagedSubdivision &&
            ft.parentPlan != null &&
            ft.lots &&
            ft.lots.every(l => !l.titles || l.titles.length === 0);

        const digital = [];
        const nonDigital = [];
        const missing = [];
        const noTitleReference = [];
        const paperTitleNonDigital = [];
        const stagedSubdivision = [];
        const allStagedSubdivision = [];

        if (formerTenures) {
            formerTenures.forEach(ft => {
                if (isStagedSubdivisionCandidate(ft)) {
                    allStagedSubdivision.push(ft);
                }

                if (ft.formerTenureNotifications && ft.formerTenureNotifications.length) {
                    if (!ft.parentPlan && !ft.title) {
                        ft.formerTenureNotifications.forEach(ftn => noTitleReference.push(ftn));
                    } else {
                        ft.formerTenureNotifications.forEach(ftn => {
                            if (ftn.digital) {
                                digital.push(ftn);
                            } else {
                                nonDigital.push(ftn);
                                paperTitleNonDigital.push(ftn);
                            }
                        });
                    }
                } else if (ft.parentPlan) {
                    if (isStagedSubdivisionCandidate(ft)) {
                        stagedSubdivision.push(ft);
                    } else {
                        missing.push(...ft.lots.map(l => ({identifier: this.formatParentLotOnPlan(ft, l)})));
                    }
                } else {
                    missing.push({identifier: ft.title});
                }
            });
        }

        if (!this.arrayItemRefsEqual(digital, this.formerTenureInterestsNotifications)) {
            this.formerTenureInterestsNotifications.splice(0, this.formerTenureInterestsNotifications.length, ...digital);
        }

        if (!this.arrayItemRefsEqual(nonDigital, this.nonDigitalTitlesForNotificationTenures)) {
            this.nonDigitalTitlesForNotificationTenures.splice(0, this.nonDigitalTitlesForNotificationTenures.length, ...nonDigital);
        }

        if (!equals(missing, this.missingNotificationTenures)) {
            this.missingNotificationTenures.splice(0, this.missingNotificationTenures.length, ...missing);
        }

        if (!this.arrayItemRefsEqual(noTitleReference, this.noTitleReferences)) {
            this.noTitleReferences.splice(0, this.noTitleReferences.length, ...noTitleReference);
        }

        if (!this.arrayItemRefsEqual(paperTitleNonDigital, this.paperTitleNonDigitalFTNs)) {
            this.paperTitleNonDigitalFTNs.splice(0, this.paperTitleNonDigitalFTNs.length, ...paperTitleNonDigital);
        }

        if (!this.arrayItemRefsEqual(stagedSubdivision, this.bringForwardStagedSubdivisionInterestsTenures)) {
            this.bringForwardStagedSubdivisionInterestsTenures.splice(
                0,
                this.bringForwardStagedSubdivisionInterestsTenures.length,
                ...stagedSubdivision
            );
        }

        if (!this.arrayItemRefsEqual(allStagedSubdivision, this.allStagedSubdivisionFormerTenures)) {
            this.allStagedSubdivisionFormerTenures.splice(0, this.allStagedSubdivisionFormerTenures.length, ...allStagedSubdivision);
        }

        if (this.planModelService.populateFormerTenureNotifications) {
            this.planModelService.populateFormerTenureNotifications = false;
            this.populateInterestsAndNotifications();
        }
    }

    private populateInterestsAndNotifications() {
        if (this.populateFormerTenuresInProgress) {
            return;
        }

        console.debug(this.title + ' populateInterestsAndNotifications');

        this.populateFormerTenuresInProgress = true;

        const planModel = this.planModelService.getModel();

        if (!planModel.formerTenures || planModel.formerTenures.length === 0) {
            this.populateFormerTenuresInProgress = false;
            return;
        }

        if (this.planModelService.modelIsDirty()) {
            const subscription = this.planModelService.save().subscribe(() => this.populateAndMerge());
            this.eventBus.cast('nlr:busy', subscription);
        } else {
            this.populateAndMerge();
        }
    }

    private populateAndMerge() {
        const planModel = this.planModelService.getModel();

        // Populate and merge interests for each former tenure
        const observables: Observable<any>[] = planModel.formerTenures.map(formerTenure => {
            const request = {
                formerTenures: [formerTenure],
                planPurposeCode: planModel.planPurpose.id,
                pdfCreationMethod: planModel.pdfCreationMethod
            };
            return this.runFormerTenureRequest(
                `${this.appConfigService.settings.apiGatewayUrl}/interests/findAllInterestsAndNotifications`,
                request,
                formerTenure
            );
        });

        forkJoinDefaultIfEmpty(observables)
            .pipe(
                switchMap(() => {
                    console.debug(`${this.title} : all former tenure requests complete`);
                    return this.planModelService.modelIsDirty() ? this.planModelService.save() : of(null);
                }),
                catchError(err => {
                    const errMsg = 'Error retrieving Former Tenure Interests And Notifications.';
                    console.error(errMsg, err);
                    this.alertModalService.createAlertMessage(errMsg);
                    return EMPTY;
                }),
                finalize(() => (this.populateFormerTenuresInProgress = false))
            )
            .subscribe();
    }

    private runFormerTenureRequest(url, request, formerTenure) {
        return this.http.post(url, request).pipe(
            map((data: any) => {
                if (data) {
                    console.debug('Success retrieving interests And Notifications for ' + formerTenure);

                    formerTenure.formerTenureNotifications = formerTenure.formerTenureNotifications || [];

                    const notificationMap = data.reduce((acc, notification) => {
                        const key = this.getNotificationKey(notification);
                        if (!acc[key]) {
                            acc[key] = [];
                        }
                        acc[key].push(notification);
                        return acc;
                    }, {});

                    const formerTenureNotificationMap = formerTenure.formerTenureNotifications.reduce((acc, notification) => {
                        const key = this.getNotificationKey(notification);
                        if (!acc[key]) {
                            acc[key] = [];
                        }
                        acc[key].push(notification);
                        return acc;
                    }, {});

                    Object.keys(formerTenureNotificationMap)
                        .filter(k => notificationMap[k])
                        .forEach(key => {
                            const titleNotificationCount = notificationMap[key].length;
                            const planNotificationCount = formerTenureNotificationMap[key].length;

                            if (titleNotificationCount == planNotificationCount) {
                                notificationMap[key] = formerTenureNotificationMap[key];
                            } else if (titleNotificationCount > planNotificationCount) {
                                notificationMap[key].splice(0, planNotificationCount, ...formerTenureNotificationMap[key]);
                            } else {
                                const difNotificationCount = planNotificationCount - titleNotificationCount;
                                formerTenureNotificationMap[key].splice(0, difNotificationCount);
                                notificationMap[key] = formerTenureNotificationMap[key];
                            }

                            notificationMap[key].forEach(v => {
                                if (!v.actionLookup) {
                                    const ftnl = this.formerTenureNotificationLookups.find(
                                        (l: any) => l.active && l.notificationCode === v.notificationCode
                                    );
                                    if (ftnl) {
                                        v.actionLookup = structuredClone(ftnl);
                                    }
                                }
                                if (!v.subjects || v.subjects.length === 0) {
                                    v.subjects.push(this.dummySubject());
                                }
                            });
                        });

                    const notifications = Object.keys(notificationMap).reduce((acc, k) => {
                        acc.push(...notificationMap[k]);
                        return acc;
                    }, []);

                    formerTenure.formerTenureNotifications.splice(0, formerTenure.formerTenureNotifications.length, ...notifications);
                }

                return data;
            }),
            catchError(err => {
                return throwError(() => err);
            })
        );
    }

    noActionDisabled(notification) {
        return notification.treatmentAction && notification.treatmentAction.id === 'NO_ACTION';
    }

    noActionClick(notification) {
        if (this.noActionDisabled(notification)) {
            return;
        }

        const promise = notification.treatmentAction
            ? firstValueFrom(
                  this.confirmationModalService.showModal({
                      title: 'Warning',
                      confirmationText: `This notification has already been actioned.
                Choosing No will cancel the update.
                Choosing Yes will clear all the previously entered data and will set the interest to 'No action'`,
                      action: 'tenure-interests-notifications:treatment-action'
                  })
              )
            : Promise.resolve();

        promise.then(() => {
            const lookup = this.notificationTreatmentActionLookups.find((v: any) => v.id === 'NO_ACTION');

            notification.treatmentAction = structuredClone(lookup);

            if (notification.treatmentDocument) {
                delete notification.treatmentDocument;
            }

            notification.subjects.splice(0, notification.subjects.length, this.dummySubject());

            // return this.planModelService.save().catch(reason => this.alertModalService.createAlertMessage(reason));
            return this.planModelService.save().subscribe({
                error: (reason: any) => {
                    this.alertModalService.createAlertMessage(reason);
                }
            });
        });
    }

    openTenureInterestsNotificationsModal(item: any) {
        console.debug(`${this.title} - openTenureInterestsNotificationsModal`);

        this.interestService.openTenureInterestsNotificationsModal(item).subscribe();
    }

    openFormerTenureInterestModal(ftn: any) {
        if (ftn.portalCreated) {
            let bringForwardStagedSubdivision = false;
            this.planModelService.getModel().formerTenures.forEach(ft =>
                ft.formerTenureNotifications.forEach(formerTenureNotification => {
                    if (formerTenureNotification.id === ftn.id && ft.bringForwardStagedSubdivisionInterests) {
                        bringForwardStagedSubdivision = true;
                    }
                })
            );
            if (bringForwardStagedSubdivision) {
                this.interestService.openManualFormerTenureInterestNotificationModal([ftn], ftn).subscribe();
            } else {
                this.interestService.openManualFormerTenureInterestNotificationModal(this.paperTitleNonDigitalFTNs, ftn).subscribe();
            }
        } else {
            this.interestService.openTenureInterestsNotificationsModal(ftn).subscribe();
        }
    }

    addNewFormerTenureInterestNotification() {
        const formerTenures = this.planModelService.getModel().formerTenures ?? [];
        const paperTitleFormerTenures = this.paperTitleNonDigitalFTNs.map((ftn: any) => {
            const parentFt = formerTenures.find((ft: any) => (ft.formerTenureNotifications ?? []).some((n: any) => n === ftn));
            return {id: parentFt?.id, identifier: ftn.identifier};
        });
        const formerTenuresForModal = this.distinctFormerTenures([
            ...paperTitleFormerTenures,
            ...this.stagedSubdivisionFormerTenures,
            ...this.encumbranceScheduleFormerTenures
        ]);
        this.interestService.openManualFormerTenureInterestNotificationModal(formerTenuresForModal).subscribe();
    }

    /**
     * The modal offers the former tenures by id and identifier, so a tenure that more than one route offers under the
     * same identifier - a conversion whose former tenure holds a single paper title - would otherwise be listed twice.
     * Where the routes offer one tenure under different identifiers they are distinct choices, not duplicates.
     */
    private distinctFormerTenures(formerTenures: {id: number; identifier: string}[]): {id: number; identifier: string}[] {
        const seen = new Set<string>();
        return formerTenures.filter(ft => {
            const key = `${ft.id}_${ft.identifier}`;
            return seen.has(key) ? false : (seen.add(key), true);
        });
    }

    deleteFormerTenureInterestNotification(ftn: any) {
        this.confirmationModalService
            .showModal({
                title: 'Warning',
                confirmationText: `Are you sure you want to remove this interest?`,
                action: 'tenure-interests-notifications:delete-portal-created'
            })
            .subscribe(action => {
                if (action !== 'tenure-interests-notifications:delete-portal-created') {
                    return;
                }
                const formerTenures = this.planModelService.getModel().formerTenures;
                if (formerTenures) {
                    formerTenures.forEach(ft => {
                        const idx = ft.formerTenureNotifications.findIndex(f => f.uid === ftn.uid);
                        if (idx !== -1) {
                            ft.formerTenureNotifications.splice(idx, 1);
                        }
                    });
                }
                const obs = this.planModelService.save();
                this.eventBus.cast('nlr:busy', obs.subscribe());
            });
    }

    get stagedSubdivisionFormerTenures(): {id: number; identifier: string}[] {
        return this.allStagedSubdivisionFormerTenures.flatMap((ft: any) =>
            ft.lots.map((l: any) => ({
                id: ft.id,
                identifier: this.formatParentLotOnPlan(ft, l)
            }))
        );
    }

    hasStagedSubdivisionFormerTenures(): boolean {
        return this.allStagedSubdivisionFormerTenures.length > 0;
    }

    /**
     * On some plan purposes the parent plan's interests and notifications are recorded on its encumbrance schedule
     * rather than against its titles, so the register returns nothing for them however the tenure is asked for. There
     * is no state that tells us any exist - only the plan the surveyor is holding does - so the purpose alone decides
     * whether they may be entered by hand.
     */
    canAddEncumbranceScheduleInterests(): boolean {
        return this.planPurposeLookup?.canAddEncumbranceScheduleInterests === true;
    }

    /**
     * Every former tenure on the plan, identified the way the rest of this panel identifies one: by its title where it
     * has one, and by lot on parent plan where it does not (see ngDoCheck). Unlike the paper title and staged
     * subdivision routes there is nothing to narrow the list by, since the interests being entered are invisible to
     * NLR - so the surveyor picks the tenure the encumbrance schedule belongs to.
     */
    get encumbranceScheduleFormerTenures(): {id: number; identifier: string}[] {
        if (!this.canAddEncumbranceScheduleInterests()) {
            return [];
        }
        return (this.planModelService.getModel().formerTenures ?? []).flatMap((ft: any) =>
            ft.title
                ? [{id: ft.id, identifier: ft.title}]
                : (ft.lots ?? []).map((l: any) => ({
                      id: ft.id,
                      identifier: this.formatParentLotOnPlan(ft, l)
                  }))
        );
    }

    hasStagedSubdivisionPortalCreatedFTNs(): boolean {
        return this.allStagedSubdivisionFormerTenures.some((ft: any) => (ft.formerTenureNotifications ?? []).some((ftn: any) => ftn.portalCreated));
    }

    // the Add button is only offered once a paper (non digital) title has been declared as holding interests or
    // notifications to bring forward - answering No, or leaving the question unanswered, leaves nothing to enter
    hasDeclaredPaperTitleNotifications(): boolean {
        return this.paperTitleNonDigitalFTNs.some((ftn: any) => ftn.partiallySurrendered === true);
    }

    // manual entries carry the identifier of the paper title they were added against, so a declaration of Yes is only
    // satisfied by an entry made from that same title
    hasManualNotificationFor(nonDigitalFtn: any): boolean {
        return this.formerTenureInterestsNotifications.some((ftn: any) => ftn.portalCreated && ftn.identifier === nonDigitalFtn.identifier);
    }

    /**
     * A staged subdivision former tenure holds no register interests to bring forward - everything against it is
     * entered by hand with Add - so any portal created notification on the tenure satisfies the declaration. The
     * tenure only appears in the staged subdivision question while it has no notifications at all, so in practice
     * this reads false until the first entry is made and the question itself drops away.
     */
    hasManualNotificationForStagedTenure(stagedTenure: any): boolean {
        return (stagedTenure.formerTenureNotifications ?? []).some((ftn: any) => ftn.portalCreated);
    }

    isDigitalPlan(): boolean {
        return this.planModelService.getModel().pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN;
    }

    isNoTitleReferenceTenureValid(noTitleReferenceTenure) {
        return noTitleReferenceTenure && typeof noTitleReferenceTenure.subjectLandTitleExists === 'boolean';
    }

    refreshFormerTenInNotifications() {
        console.debug(this.title + ' refreshFormerTenInNotifications');
        this.populateInterestsAndNotifications();
    }

    isRefreshButtonDisabled() {
        const formerTenures = this.planModelService.getModel().formerTenures;
        return this.populateFormerTenuresInProgress || !formerTenures || formerTenures.length === 0;
    }

    actionsPdfCreationMethodInvalidForPlan(treatmentAction) {
        if (treatmentAction && treatmentAction.supportedPdfCreationMethods) {
            const planPDFCreationMethod = this.planModelService.getModel().pdfCreationMethod;
            const retVal = treatmentAction.supportedPdfCreationMethods.find(spcm => planPDFCreationMethod === spcm);

            return !retVal;
        } else {
            return false;
        }
    }

    noActionVisible(notification) {
        const isInNotificationTreatmentActions =
            notification.actionLookup && notification.actionLookup.notificationTreatmentActions.includes('NO_ACTION');
        const noActionLookup: any = this.notificationTreatmentActionLookups.find((v: any) => v.id === 'NO_ACTION');
        const isForParentTenureAssociationType =
            noActionLookup &&
            noActionLookup.active &&
            noActionLookup.parentTenureAssociationType === this.planModelService.getModel().planPurpose.parentTenureAssociationType;
        return isInNotificationTreatmentActions && isForParentTenureAssociationType;
    }

    getDisplayMessage(messageKey) {
        return this.formerTenureUiConfig[messageKey];
    }

    getDisplayMessageForIdentifier(messageKey, identifier) {
        return this.makeTemplate(this.formerTenureUiConfig[messageKey], {TenureId: identifier});
    }

    makeTemplate(templateString, templateVariables) {
        const keys = Object.keys(templateVariables);
        const values = Object.values(templateVariables);
        const templateFunction = new Function(...keys, `return \`${templateString}\`;`);
        return templateFunction(...values);
    }

    getActionDetailsList(notification) {
        if (notification.treatmentAction && notification.subjects.length) {
            const subjects = notification.subjects.filter(s => s.subjectType && s.subjectType.actionDetailsFormat);
            const actionDetailsFormatSubjectsMap = subjects.reduce((acc, s) => {
                const k = s.subjectType.actionDetailsFormat;
                if (!acc[k]) {
                    acc[k] = [];
                }
                acc[k].push(s);
                return acc;
            }, {});

            return Object.keys(actionDetailsFormatSubjectsMap).map(actionDetailsFormat =>
                actionDetailsFormat
                    .replace('${TreatmentActionTerm}', notification.treatmentAction.description)
                    .replace('${Subjects}', actionDetailsFormatSubjectsMap[actionDetailsFormat].map(s => s.subject).join())
            );
        }
        return [];
    }

    getSubjectText(notification) {
        // Filter out any subjects from the Subject column where InterestSubjectType.ActionDetailsFormat is not blank
        return notification.subjects
            .filter(s => s.subject && (!s.subjectType || !s.subjectType.actionDetailsFormat))
            .map(s => s.subject)
            .join();
    }

    getNewTenureText(notification) {
        return notification.subjects
            .reduce((acc, s) => {
                acc.push(s.affectedLots.map(a => (a.endLot === a.startLot ? `${a.startLot}` : `${a.startLot}-${a.endLot}`)));
                return acc;
            }, [])
            .join(',');
    }

    setStagedSubdivision(formerTenure: any, value: boolean) {
        formerTenure.bringForwardStagedSubdivisionInterests = value;
        this.planModelService.save().subscribe({
            error: (reason: any) => this.alertModalService.createAlertMessage(reason)
        });
    }

    getStagedTenureIdentifier(formerTenure: any): string {
        return this.formatParentPlanNumber(formerTenure);
    }

    /**
     * The parent plan number of a former tenure as the register knows it, eg SP8263 for a survey-strata parent plan.
     */
    private formatParentPlanNumber(formerTenure: any): string {
        const code = formerTenure?.parentPlanType?.id ?? '';
        const prefix = SP_PREFIXED_PARENT_PLAN_TYPES.includes(code) ? 'SP' : code;

        return `${prefix}${formerTenure?.parentPlan ?? ''}`;
    }

    /**
     * A former tenure lot identified as lot on parent plan, eg CP4/SP8263. The lot prefix carries the common property
     * and road designations, so it is part of the lot number rather than a separate qualifier.
     */
    private formatParentLotOnPlan(formerTenure: any, lot: any): string {
        const parentLot = `${lot?.parentLotPrefix ?? ''}${lot?.parentLot ?? ''}`;
        const planNumber = this.formatParentPlanNumber(formerTenure);

        // common property is not held under a title, so it is not identified as a lot on a plan the register can be
        // asked about - it takes the same pipe separated form the service reports it with
        return lot?.parentLotType === COMMON_PROPERTY
            ? `${lot.parentLotType} ${parentLot} | ${planNumber}`
            : `${parentLot}/${planNumber}`;
    }

    dummySubject() {
        return {affectedLots: []};
    }

    getNotificationKey(notification) {
        const effectiveNotificationCode = notification.notificationCodeOverride || notification.notificationCode;
        return `${notification.identifier}_${effectiveNotificationCode}_${notification.notificationDocument}_${notification.endorsement}`;
    }

    arrayItemRefsEqual(a1, a2) {
        return a1.length === a2.length && a1.every((v, i) => v === a2[i]);
    }
    hasFormerTenureInterests(): boolean {
        return this.formerTenureInterestsNotifications.length > 0;
    }

    showHeader(): boolean {
        return this.interestsUiConfig !== undefined;
    }
}
